import React, { useCallback, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import type { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminErrorBoundary } from '@/components';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { TodoListForm } from '@/components/admin/todoLists/TodoListForm';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useCreateTodoListMutation } from '@/store/api/admin/todoListsApi';
import { extractTodoListError } from './todoListErrors';
import type { AdminStackParamList } from '@/navigation/admin/AdminNavigator';

type Props = StackScreenProps<AdminStackParamList, 'CreateTodoList'>;

/**
 * Create a new to-do list. Resolves `propertyId` from the admin context and, on a
 * successful create, navigates back to the index (which refetches via the LIST
 * tag invalidation). Server errors surface as an inline, non-crashing message.
 */
export const CreateTodoListScreen: React.FC<Props> = ({ navigation }) => {
  const { layoutMode } = useResponsive();
  const { selectedPropertyId } = useAdminUser();
  const [createTodoList, { isLoading }] = useCreateTodoListMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (payload: any) => {
      setSubmitError(null);
      try {
        await createTodoList(payload).unwrap();
        navigation.goBack();
      } catch (error) {
        setSubmitError(extractTodoListError(error));
      }
    },
    [createTodoList, navigation],
  );

  const handleCancel = useCallback(() => navigation.goBack(), [navigation]);

  const form = (
    <TodoListForm
      mode="create"
      propertyId={selectedPropertyId ?? 0}
      isSubmitting={isLoading}
      submitError={submitError}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );

  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary>
        <DesktopAdminLayout>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.desktopContent}
            showsVerticalScrollIndicator
            keyboardShouldPersistTaps="handled"
          >
            <DesktopPageHeader
              title="Create to-do list"
              subtitle="Set up a recurring checklist"
            />
            <GlassCard padding={24} style={styles.card}>
              {form}
            </GlassCard>
          </ScrollView>
        </DesktopAdminLayout>
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary>
      <BaseAdminScreen
        screenName="CreateTodoList"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.mobileContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View>{form}</View>
        </ScrollView>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  desktopContent: {
    paddingBottom: 24,
    gap: 16,
    maxWidth: 880,
    alignSelf: 'center',
    width: '100%',
  },
  mobileContent: { padding: 16, paddingBottom: 32 },
  card: { gap: 16 },
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
});
